import { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useSidebar } from '../../contexts/SidebarContext';
import { 
  LayoutDashboard, 
  ArrowDownCircle, 
  ArrowUpCircle,
  TrendingUp,
  RefreshCcw,
  Users,
  FileText,
  Settings,
  ChevronDown,
  FolderTree,
  Building2,
  Wallet,
  FileBarChart,
  ShoppingCart,
  Package,
  CreditCard,
  ClipboardList,
  Gauge,
  Eye,
  Receipt,
  BarChart3,
  Link2,
  Zap,
  LifeBuoy
} from 'lucide-react';
import { ScrollArea } from '../ui/scroll-area';
import { receivablesAPI, payablesAPI } from '../../lib/api';
import useModules from '../../hooks/useModules';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { Lock, X } from 'lucide-react';

// Logo OFS com fundo TRANSPARENTE (seta + OFS + subtitulo). Se funde com o
// degrade do menu sem retangulo. NAO ha texto "OFS" separado (evita duplicar).
const LOGO_SYMBOL = "/ofs-logo.png";

// New menu structure matching the design
// Cada item tem 'module' = codigo do modulo no backend (PATH_TO_MODULE)
const menuGroups = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    href: '/dashboard',
    icon: Gauge,
    single: true,
    module: 'dashboard',
  },
  {
    id: 'financeiro',
    title: 'Financeiro',
    icon: Wallet,
    items: [
      { name: 'Visão geral', href: '/cash-flow', icon: Eye, module: 'cash-flow' },
      { name: 'Contas a Receber', href: '/receivables', icon: ArrowUpCircle, badge: 'receivables', module: 'receivables' },
      { name: 'Contas a Pagar', href: '/payables', icon: ArrowDownCircle, module: 'payables' },
      { name: 'Cartões de Crédito', href: '/credit-cards', icon: CreditCard, module: 'credit-cards' },
      { name: 'Conciliação', href: '/reconciliation', icon: RefreshCcw, module: 'reconciliation' },
      { name: 'Extrato Financeiro', href: '/extract', icon: FileText, module: 'extract' },
    ]
  },
  {
    id: 'operacional',
    title: 'Operacional',
    icon: ShoppingCart,
    items: [
      { name: 'Vendas', href: '/sales', icon: ShoppingCart, module: 'sales' },
      { name: 'Compras', href: '/purchases', icon: Package, module: 'purchases' },
      { name: 'Contratos', href: '/contracts', icon: FileText, module: 'contracts' },
      { name: 'Notas Fiscais', href: '/invoices', icon: Receipt, module: 'invoices' },
    ]
  },
  {
    id: 'cadastros',
    title: 'Cadastros',
    icon: Users,
    items: [
      { name: 'Entidades', href: '/entities', icon: Users, module: 'entities' },
      { name: 'Produtos', href: '/products', icon: Package, module: 'products' },
      { name: 'Serviços', href: '/services', icon: ClipboardList, module: 'services' },
      { name: 'Categorias', href: '/categories', icon: FolderTree, module: 'categories' },
      { name: 'Centros de Custo', href: '/cost-centers', icon: Building2, module: 'cost-centers' },
      { name: 'Contas Bancárias', href: '/accounts', icon: Wallet, module: 'accounts' },
    ]
  },
  {
    id: 'relatorios',
    title: 'Relatórios',
    icon: FileBarChart,
    items: [
      { name: 'Relatórios', href: '/reports', icon: FileBarChart, module: 'reports' },
      { name: 'Métricas', href: '/reconciliation/metrics', icon: BarChart3, module: 'reconciliation' },
    ]
  },
  {
    id: 'sistema',
    title: 'Sistema',
    icon: Settings,
    items: [
      { name: 'Integrações', href: '/integrations', icon: Link2, module: 'integrations' },
      { name: 'Automações', href: '/automations', icon: Zap, module: 'automations' },
      { name: 'Open Finance', href: '/open-finance', icon: Building2, module: 'open-finance' },
      { name: 'Configurações', href: '/settings', icon: Settings, module: 'settings' },
      // Sem 'module': ajuda e cancelamento precisam estar acessíveis em qualquer plano
      { name: 'Ajuda', href: '/help', icon: LifeBuoy },
    ]
  },
];

export function NewSidebar() {
  const { collapsed: collapsedDesktop, mobileOpen, closeMobileSidebar } = useSidebar();
  const isMobile = useIsMobile();
  // No mobile a gaveta é sempre exibida "expandida" (com labels) — o collapse
  // de ícone-só é um recurso exclusivo do desktop para ganhar espaço horizontal.
  const collapsed = isMobile ? false : collapsedDesktop;
  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('axion_sidebar_expanded');
    return saved ? JSON.parse(saved) : ['financeiro'];
  });
  const [badges, setBadges] = useState({ receivables: 0 });
  const location = useLocation();
  const { has: hasModule, isLoading: modulesLoading } = useModules();

  // Verifica se um item da sidebar esta liberado pelo plano do usuario
  // Durante loading liberamos tudo para nao piscar a UI
  const isModuleAllowed = (moduleCode) => {
    if (!moduleCode) return true;
    if (modulesLoading) return true;
    return hasModule(moduleCode);
  };

  // Fetch badge counts
  const fetchBadges = useCallback(async () => {
    try {
      const [receivablesRes] = await Promise.all([
        receivablesAPI.list({ status: 'overdue', limit: 1 }),
      ]);
      setBadges({
        receivables: receivablesRes.data.total || 0,
      });
    } catch (error) {
      console.error('Failed to fetch badges:', error);
    }
  }, []);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchBadges]);

  // Save expanded state to localStorage
  useEffect(() => {
    localStorage.setItem('axion_sidebar_expanded', JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  // Auto-expand group based on current path
  useEffect(() => {
    menuGroups.forEach(group => {
      if (group.items && !group.single) {
        const isActive = group.items.some(item =>
          location.pathname === item.href ||
          location.pathname.startsWith(item.href.split('?')[0])
        );
        if (isActive) {
          setExpandedGroups(prev => prev.includes(group.id) ? prev : [...prev, group.id]);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Fecha a gaveta mobile sempre que a rota muda (navegação por um item do menu)
  useEffect(() => {
    closeMobileSidebar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (groupId) => {
    if (collapsed) return;
    setExpandedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const isGroupActive = (group) => {
    if (group.single) {
      return location.pathname === group.href;
    }
    return group.items?.some(item => 
      location.pathname === item.href || 
      location.pathname.startsWith(item.href.split('?')[0])
    );
  };

  const isItemActive = (href) => {
    const basePath = href.split('?')[0];
    return location.pathname === basePath || location.pathname.startsWith(basePath + '/');
  };

  return (
    <>
      {/* Overlay do mobile — escurece o conteúdo atrás da gaveta e fecha ao tocar fora.
          Só existe no DOM/visível abaixo de md; no desktop nunca aparece. */}
      <div
        aria-hidden="true"
        onClick={closeMobileSidebar}
        className={cn(
          "fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 md:hidden",
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
      />

      <aside
        data-testid="sidebar"
        className={cn(
          "fixed left-0 top-0 z-50 h-screen transition-all duration-300 ease-in-out",
          // Mobile: gaveta off-canvas, sempre em largura "expandida" (60) para leitura confortável.
          // Desktop (md+): comportamento original preservado, incluindo o collapse.
          "w-60",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          "md:translate-x-0",
          collapsedDesktop ? "md:w-[72px]" : "md:w-60"
        )}
        style={{
          background: 'linear-gradient(180deg, #1A233A 0%, #0F172A 100%)',
        }}
      >
        {/* Botão fechar — só no mobile (desktop usa o toggle de collapse no header) */}
        <button
          type="button"
          onClick={closeMobileSidebar}
          aria-label="Fechar menu"
          className="absolute right-2 top-2 z-10 flex h-11 w-11 items-center justify-center rounded-lg text-white/70 hover:bg-white/10 hover:text-white md:hidden"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Logo Header — logo transparente sobre o degrade do menu.
            Expandido: logo completa. Colapsado: mostra so o topo (a seta). */}
        <div className={cn(
          "flex items-center justify-center border-b border-white/5",
          "h-24 px-3",
          collapsedDesktop ? "md:h-16 md:px-1" : "md:h-24 md:px-3"
        )}>
          <img
            src={LOGO_SYMBOL}
            alt="OFS — Organizador Financeiro Simples"
            className={cn(
              "object-contain",
              "h-[5.5rem] max-w-full",
              collapsedDesktop ? "md:h-10 md:w-10 md:object-top" : "md:h-[5.5rem] md:max-w-full"
            )}
          />
        </div>

      {/* Navigation */}
      <ScrollArea className="h-[calc(100vh-5rem)]">
        <nav className={cn("py-4", collapsed ? "px-2" : "px-3")}>
          {menuGroups.map((group) => {
            const GroupIcon = group.icon;
            const isActive = isGroupActive(group);
            const isExpanded = expandedGroups.includes(group.id);

            // Single item (Dashboard, Configurações)
            if (group.single) {
              return (
                <NavLink
                  key={group.id}
                  to={group.href}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 mb-1 transition-all duration-200",
                    collapsed && "justify-center px-2",
                    isActive 
                      ? "bg-white/10 text-white" 
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  )}
                  title={collapsed ? group.title : undefined}
                >
                  <GroupIcon className={cn(
                    "h-5 w-5 flex-shrink-0",
                    isActive ? "text-cyan-400" : "text-white/70"
                  )} />
                  {!collapsed && (
                    <span className="text-sm font-medium">{group.title}</span>
                  )}
                </NavLink>
              );
            }

            // Group with items
            return (
              <div key={group.id} className="mb-1">
                {/* Group Header */}
                <button
                  onClick={() => toggleGroup(group.id)}
                  className={cn(
                    "w-full flex items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200",
                    collapsed && "justify-center px-2",
                    isActive 
                      ? "bg-white/10 text-white" 
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <GroupIcon className={cn(
                      "h-5 w-5 flex-shrink-0",
                      isActive ? "text-cyan-400" : "text-white/70"
                    )} />
                    {!collapsed && (
                      <span className="text-sm font-medium">{group.title}</span>
                    )}
                  </div>
                  {!collapsed && (
                    <ChevronDown className={cn(
                      "h-4 w-4 transition-transform duration-200",
                      isExpanded && "rotate-180"
                    )} />
                  )}
                </button>

                {/* Group Items */}
                {!collapsed && (
                  <div className={cn(
                    "overflow-hidden transition-all duration-300",
                    isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  )}>
                    <div className="mt-1 ml-4 pl-4 border-l border-white/10 space-y-0.5">
                      {group.items.map((item) => {
                        const ItemIcon = item.icon;
                        const itemActive = isItemActive(item.href);
                        const badgeCount = item.badge ? badges[item.badge] : 0;
                        const moduleAllowed = isModuleAllowed(item.module);

                        // Item bloqueado pelo plano: redireciona para /billing
                        const targetHref = moduleAllowed ? item.href : '/billing';

                        return (
                          <NavLink
                            key={item.href}
                            to={targetHref}
                            title={!moduleAllowed ? 'Disponível em planos superiores. Clique para ver os planos.' : undefined}
                            className={cn(
                              "flex items-center justify-between rounded-lg px-3 py-2 transition-all duration-200",
                              !moduleAllowed
                                ? "text-white/30 hover:bg-white/5 hover:text-white/50"
                                : itemActive
                                ? "bg-white/10 text-white"
                                : "text-white/60 hover:bg-white/5 hover:text-white"
                            )}
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <ItemIcon className={cn(
                                "h-4 w-4 flex-shrink-0",
                                !moduleAllowed
                                  ? "text-white/30"
                                  : itemActive
                                  ? "text-cyan-400"
                                  : "text-white/50"
                              )} />
                              <span className="text-sm truncate">{item.name}</span>
                            </div>
                            {!moduleAllowed ? (
                              <Lock className="h-3 w-3 text-white/30 flex-shrink-0" />
                            ) : badgeCount > 0 ? (
                              <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold">
                                {badgeCount > 99 ? '99+' : badgeCount}
                              </span>
                            ) : null}
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </ScrollArea>
      </aside>
    </>
  );
}
